import React, { useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { setUser } from "../redux/userSlice";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { jwtDecode } from "jwt-decode";

/**
 * @param {React.ReactNode} children
 * @param {string} roleType - Optional. One of: "admin", "doctor", "staff", "user"
 */
function ProtectedRoute({ children, roleType }) {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchUser = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/user/getUser_id",
        { token: localStorage.getItem("token") },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      dispatch(hideLoading());

      if (res.data.success) {
        dispatch(setUser(res.data.data));
      } else {
        localStorage.clear();
        navigate("/login");
      }
    } catch (err) {
      dispatch(hideLoading());
      localStorage.clear();
      navigate("/login");
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!user && localStorage.getItem("token")) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Not logged in
  if (!localStorage.getItem("token")) {
    return <Navigate to="/login" replace />;
  }

  // Still fetching
  if (!user) return null;

  // Role restriction
  if (roleType) {
    const decoded = jwtDecode(localStorage.getItem("token"));

    const roleMap = {
      admin: decoded.isAdmin,
      doctor: decoded.isDoctor,
      staff: decoded.isStaff,
      reg: decoded.regDoctor,
      user: !decoded.isAdmin && !decoded.isDoctor && !decoded.isStaff &&  !decoded.regDoctor,
    };

    // Special logic for routeType="user" (redirect if not a user)
    if (roleType === "user") {
      if (decoded.isAdmin) return <Navigate to="/admin/home" replace />;
      if (decoded.isDoctor) return <Navigate to="/doctor/home" replace />;
      if (decoded.isStaff) return <Navigate to="/staff/home" replace />;
        if (decoded.regDoctor) return <Navigate to="/reg/home" replace />;

      // Allow access for actual users
      if (!decoded.isAdmin && !decoded.isDoctor && !decoded.isStaff && !decoded.regDoctor) {
        return children;
      }

      return <Navigate to="/unauthorised" replace />;
    }

    // Generic roleType check
    if (!roleMap[roleType]) {
      return <Navigate to="/unauthorised" replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
